import React, { useEffect, useState } from "react";
import { Menu, MenuButton, MenuDivider, MenuItem } from "@szhsin/react-menu";
import { useDispatch, useSelector } from "react-redux";
import { fetchActions, fetchSettings, toggleCorporate } from "./redux/usersSagas";
import { usersApi } from "./api";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import { Add } from "./smartComponents/Add";
import { Edit } from "./smartComponents/Edit";
import { Delete } from "./smartComponents/Delete";
import { usePopup } from "./hooks/usePopup";
import FilterUsers from "./smartComponents/FilterUsers";
import { Duplicate } from "./smartComponents/Duplicate";
import { Mirror } from "./smartComponents/Mirror";
import { ResetPassword } from "./smartComponents/ResetPassword";
import { CreateCustomPermissionGroup } from "./smartComponents/CreateCustomPermissionsGroup";
import { LoginAs } from "./smartComponents/LoginAs";
import { getBaseUrlParams } from "../../../utils/getBaseUrlParams";
import { IconDots } from "../../../assets/images/svg";
import UserRow from "./smartComponents/UserRow";
import { WindowVirtualList } from "../../../components/WindowVirtualList/WindowVirtualList";
import { requestWithToast } from "../../../utils/request";
import "./users.scss";

function prepareListItems(users, searchValue) {
  const rows = [];
  if (!users) return rows;

  return !searchValue
    ? users
    : users.filter(
        (user) =>
          user.firstName.toLowerCase().includes(searchValue) ||
          user.lastName.toLowerCase().includes(searchValue)
      );
}

const requestMenuValues = {
  resetPasswordInstructions: usersApi.sendResetPasswordInstructions,
  welcomeEmail: usersApi.sendWelcomeEmail
};

export function UsersModule() {
  const dispatch = useDispatch();

  const users = useSelector((state) => state.users.users);
  const canCreateUser = useSelector((state) => state.users.commonSettings.canCreateUser);

  const isLoading = useSelector((state) => state.users.isLoading);
  const areUsersLoading = useSelector((state) => state.users.areUsersLoading);
  const actionsLoading = useSelector((state) => state.users.actionsLoading);
  const currentUserActions = useSelector((state) => state.users.currentUserActions);

  const [selectedUser, setSelectedUser] = useState("");

  const { popup, openPopup, closePopup } = usePopup();

  const handlePopup = (item, type) => {
    if (requestMenuValues[type]) {
      const { organization_id } = getBaseUrlParams();
      requestWithToast(requestMenuValues[type], { organization_id, id: item.id.toString() });
      return;
    }

    setSelectedUser(item);

    openPopup(type);
  };

  useEffect(() => {
    dispatch(fetchSettings());
  }, []);

  if (isLoading)
    return (
      <div className="light">
        <LoadingAnimation uiLoader />
      </div>
    );

  const activeTabClassName = "tabs__tab--active";

  const onMenuClick = ({ value, syntheticEvent }, item) => {
    syntheticEvent.preventDefault();

    handlePopup(item, value);
  };

  const menu = (name) => (
    <>
      {currentUserActions.canDuplicate && (
        <>
          <MenuItem value="duplicate">
            <span>Duplicate</span>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {currentUserActions.canMirror && (
        <>
          <MenuItem value="mirror">
            <span>Mirror</span>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      <MenuItem value="resetPassword">
        <span>Reset Password</span>
      </MenuItem>
      <MenuDivider />

      {currentUserActions.canSendResetPasswordInstructions && (
        <>
          <MenuItem value="resetPasswordInstructions">
            <span>Send Reset Password Instruction</span>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {currentUserActions.canSendWelcomeEmail && (
        <>
          <MenuItem value="welcomeEmail">
            <span>Send Welcome Email</span>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {currentUserActions.canSetCustomPermissions && (
        <>
          <MenuItem value="customPermissionGroup">
            <span>Save {name}&apos;s Settings as New Role </span>
          </MenuItem>
          <MenuDivider />
        </>
      )}
    </>
  );

  const renderMenu = (item) => {
    return (
      <Menu
        portal
        menuButton={
          <MenuButton>
            <IconDots />
          </MenuButton>
        }
        onMenuChange={({ open }) => {
          if (open) {
            dispatch(fetchActions(item.id));
          }
        }}
        onItemClick={(eventObj) => onMenuClick(eventObj, item)}
        direction="left"
        align="start"
        transition
      >
        {actionsLoading ? <LoadingAnimation uiLoader /> : menu(item.name)}
      </Menu>
    );
  };

  const onSetCorporate = ({ e, ...rest }) => {
    if (!e.target.className.includes(activeTabClassName)) {
      dispatch(toggleCorporate(rest));
    }
  };

  return (
    <div id="users">
      <div className="users">
        {canCreateUser && (
          <div className="users__add">
            <button onClick={() => openPopup("add")} className="button button--primary">
              Add New User
            </button>
          </div>
        )}

        <div className="users__top">
          <FilterUsers />
        </div>

        <div className="users__content">
          <ul className="group">
            {areUsersLoading ? (
              <LoadingAnimation />
            ) : (
              users && (
                <>
                  {users.length >= 1 && (
                    <p style={{ fontWeight: "bold" }}>Total Users: {users.length}</p>
                  )}
                  <WindowVirtualList
                    rawItems={users}
                    processItemsFn={prepareListItems}
                    estimateSize={() => 61}
                    getKey={(item) => item.id}
                    renderRow={({ item, index }) => (
                      <UserRow
                        item={item}
                        index={index}
                        onSetCorporate={onSetCorporate}
                        handlePopup={handlePopup}
                        renderMenu={renderMenu}
                        activeTabClassName={activeTabClassName}
                      />
                    )}
                  />
                </>
              )
            )}
          </ul>
        </div>

        {popup.add && <Add isOpen={popup.add} onClose={closePopup("add")} />}

        {popup.edit && (
          <Edit isOpen={popup.edit} onClose={closePopup("edit")} selected={selectedUser} />
        )}

        {popup.duplicate && (
          <Duplicate
            isOpen={popup.duplicate}
            onClose={closePopup("duplicate")}
            selected={{ id: selectedUser.id, name: selectedUser.name }}
          />
        )}

        {popup.mirror && (
          <Mirror
            isOpen={popup.mirror}
            onClose={closePopup("mirror")}
            selected={{ id: selectedUser.id, name: selectedUser.name }}
          />
        )}

        {popup.customPermissionGroup && (
          <CreateCustomPermissionGroup
            isOpen={popup.customPermissionGroup}
            onClose={closePopup("customPermissionGroup")}
            selected={{ id: selectedUser.id }}
          />
        )}

        {popup.resetPassword && (
          <ResetPassword
            isOpen={popup.resetPassword}
            onClose={closePopup("resetPassword")}
            selected={{ id: selectedUser.id, name: selectedUser.name }}
          />
        )}

        {popup.delete && (
          <Delete
            isOpen={popup.delete}
            onClose={closePopup("delete")}
            selected={{ id: selectedUser.id, name: selectedUser.name }}
          />
        )}

        {popup.loginAs && (
          <LoginAs
            isOpen={popup.loginAs}
            onClose={closePopup("loginAs")}
            selected={{ id: selectedUser.id }}
          />
        )}
      </div>
    </div>
  );
}
